import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { pillarArticlesA } from '@/mocks/pillarArticlesA';
import { pillarArticlesB } from '@/mocks/pillarArticlesB';

type LocalizedText = { en: string; pt: string; de: string };

type PillarArticle = {
  slug: string;
  image: string;
  title: LocalizedText;
  summary: LocalizedText;
  intro: LocalizedText;
  sections: { heading: LocalizedText; body: LocalizedText }[];
  faq: { q: LocalizedText; a: LocalizedText }[];
};

const allArticles: PillarArticle[] = [...pillarArticlesA, ...pillarArticlesB];

function pick(text: LocalizedText, lang: string): string {
  if (lang === 'pt') return text.pt;
  if (lang === 'de') return text.de;
  return text.en;
}

export default function PillarArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language || 'en';
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
          <button onClick={() => navigate(`/${lang}`)} className="btn-primary">
            {t('buttons.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  const otherArticles = allArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={pick(article.title, lang)}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/70" />
        </div>
        <div className="relative z-10 px-4 md:px-8 lg:px-16 xl:px-24 py-20 md:py-28">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-300 text-sm font-medium mb-6">
              <i className="ri-book-open-line" />
              <span>Guide</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              {pick(article.title, lang)}
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl">
              {pick(article.summary, lang)}
            </p>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 lg:px-16 xl:px-24 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <aside className="lg:w-72 shrink-0">
              <div className="sticky top-24">
                <div className="bg-white rounded-xl border border-background-200 p-5 mb-6">
                  <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4 uppercase tracking-wider">
                    {t('article.tableOfContents')}
                  </h3>
                  <nav className="space-y-2">
                    <a href="#introduction" className="block text-sm text-foreground-600 hover:text-primary-600 py-1.5 transition-colors">
                      Introduction
                    </a>
                    {article.sections.map((section, idx) => (
                      <a
                        key={idx}
                        href={`#section-${idx}`}
                        className="block text-sm text-foreground-600 hover:text-primary-600 py-1.5 transition-colors"
                      >
                        {pick(section.heading, lang)}
                      </a>
                    ))}
                    <a href="#faq" className="block text-sm text-foreground-600 hover:text-primary-600 py-1.5 transition-colors">
                      {t('article.faq')}
                    </a>
                  </nav>
                </div>

                <div className="bg-primary-50 rounded-xl border border-primary-100 p-5">
                  <h3 className="font-heading text-sm font-semibold text-primary-800 mb-3">
                    {t('ctaSection.title')}
                  </h3>
                  <p className="text-sm text-primary-700 mb-4 leading-relaxed">
                    {t('ctaSection.description')}
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="btn-primary w-full text-sm py-2.5"
                  >
                    <i className="ri-store-2-line mr-1.5" />
                    {t('buttons.mainSite')}
                  </a>
                </div>
              </div>
            </aside>

            <article className="flex-1">
              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-10">
                <div id="introduction" className="mb-10 scroll-mt-24">
                  <p className="text-foreground-700 leading-relaxed text-base">
                    {pick(article.intro, lang)}
                  </p>
                </div>

                {article.sections.map((section, idx) => (
                  <div key={idx} id={`section-${idx}`} className="mb-10 scroll-mt-24">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                      {pick(section.heading, lang)}
                    </h2>
                    <p className="text-foreground-700 leading-relaxed text-base">
                      {pick(section.body, lang)}
                    </p>
                  </div>
                ))}

                <div id="faq" className="mt-12 scroll-mt-24">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    {t('article.faq')}
                  </h2>
                  <div className="space-y-3">
                    {article.faq.map((faq, index) => (
                      <div key={index} className="border border-background-200 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === index ? null : index)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-background-50 transition-colors"
                        >
                          <span className="font-semibold text-foreground-800 text-sm pr-4">
                            {pick(faq.q, lang)}
                          </span>
                          <i
                            className={`ri-arrow-down-s-line text-foreground-400 transition-transform shrink-0 ${
                              openFaq === index ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {openFaq === index && (
                          <div className="px-5 pb-5">
                            <p className="text-sm text-foreground-600 leading-relaxed">{pick(faq.a, lang)}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-12 p-6 md:p-8 bg-primary-50 rounded-2xl border border-primary-100">
                  <h3 className="font-heading text-xl font-bold text-primary-800 mb-3">
                    {t('ctaSection.title')}
                  </h3>
                  <p className="text-primary-700 text-sm leading-relaxed mb-6">
                    {t('ctaSection.description')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="btn-primary text-sm"
                    >
                      <i className="ri-store-2-line mr-1.5" />
                      {t('buttons.mainSite')}
                    </a>
                    <Link to={`/${lang}`} className="btn-outline text-sm">
                      <i className="ri-home-4-line mr-1.5" />
                      {t('buttons.backToHome')}
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  {t('article.relatedArticles')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {otherArticles.map((other) => (
                    <Link
                      key={other.slug}
                      to={`/${lang}/articles/${other.slug}`}
                      className="group text-left p-5 rounded-xl bg-white border border-background-200 hover:border-primary-300 transition-all duration-200"
                    >
                      <div className="aspect-[16/10] overflow-hidden rounded-lg mb-3">
                        <img
                          src={other.image}
                          alt={pick(other.title, lang)}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <h4 className="font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors text-sm mb-2">
                        {pick(other.title, lang)}
                      </h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        {pick(other.summary, lang)}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-10 text-center">
                <Link
                  to={`/${lang}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  {t('buttons.backToHome')}
                </Link>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}